import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { HealthPageShell } from '../_shared/HealthPageShell';
import {
	DEPLOY_ENV_LABEL,
	describeSupabase,
	isLocalPointedAtHosted,
	resolveDeployEnv,
	type DeployEnv
} from './supabaseInstance';

function envChipColor(env: DeployEnv): 'error' | 'warning' | 'default' {
	if (env === 'production') return 'error';
	if (env === 'staging') return 'warning';
	return 'default';
}

/**
 * The page body, split from the page shell so it can be rendered in a test with
 * nothing but a theme. The shell is Fuse template layout — it needs the settings
 * provider, the scrollbar context and a media-query stub, none of which say
 * anything about whether this page sends you to the right database.
 */
export function SupabaseHealthContent() {
	const instance = describeSupabase(import.meta.env.VITE_SUPABASE_URL);
	const env = resolveDeployEnv(import.meta.env.VITE_VERCEL_ENV, import.meta.env.DEV);
	const crossWired = isLocalPointedAtHosted(instance, env);

	return (
		<>
			<Paper className="p-6">
				<div className="flex items-start justify-between gap-4 mb-4">
					<div className="min-w-0">
						<Typography className="font-semibold">This build's database</Typography>
						<Typography variant="body2" color="text.secondary">
							Where the admin reads and writes right now.
						</Typography>
					</div>
					<Chip
						label={DEPLOY_ENV_LABEL[env]}
						size="small"
						color={envChipColor(env)}
						variant={env === 'local' ? 'outlined' : 'filled'}
					/>
				</div>

				{crossWired && (
					<Alert severity="warning" className="mb-4 text-xs">
						Local build pointed at a hosted project. Anything you change here lands in a
						shared database, not your local one.
					</Alert>
				)}

				<div className="flex flex-col gap-2 mb-5 text-sm">
					<div className="flex gap-3">
						<span className="w-24 shrink-0 text-slate-500">API URL</span>
						<span className="font-mono break-all">{instance.apiUrl ?? '— not configured —'}</span>
					</div>
					{instance.projectRef && (
						<div className="flex gap-3">
							<span className="w-24 shrink-0 text-slate-500">Project</span>
							<span className="font-mono break-all">{instance.projectRef}</span>
						</div>
					)}
				</div>

				{instance.consoleUrl ? (
					<Button
						variant="outlined"
						size="small"
						endIcon={<OpenInNewIcon />}
						href={instance.consoleUrl}
						target="_blank"
						rel="noreferrer"
					>
						Open {instance.consoleLabel}
					</Button>
				) : (
					<Alert severity="info" className="text-xs">
						{instance.apiUrl
							? 'No console link: this URL is not a local instance or a supabase.co project, so where its dashboard lives cannot be inferred.'
							: 'VITE_SUPABASE_URL is not set for this build.'}
					</Alert>
				)}

				{instance.kind === 'local' && (
					<Typography
						variant="body2"
						color="text.secondary"
						className="mt-3 text-xs"
					>
						Studio is served by the Supabase CLI — it only answers while `supabase start`
						is running.
					</Typography>
				)}
			</Paper>

			<Paper className="p-6">
				<div className="flex items-center justify-between gap-3 mb-3">
					<Typography className="font-semibold">Planned</Typography>
					<Chip label="Coming soon" size="small" />
				</div>
				<Typography variant="body2" color="text.secondary">
					Database size, recent signup rate, RLS policy errors, auth failure rate. Requires a
					Supabase management API token.
				</Typography>
			</Paper>
		</>
	);
}

export function SupabaseHealthView() {
	return (
		<HealthPageShell
			title="Supabase"
			subtitle="Database & auth"
		>
			<SupabaseHealthContent />
		</HealthPageShell>
	);
}
